import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import type { SaidaRota } from "../../lib/trpc.js";
import { trpc } from "../../lib/trpc.js";
import { dataHora, dolar } from "../../lib/formato.js";
import { Pagina } from "../../components/Pagina.js";
import { Sobreposicao } from "../../components/Sobreposicao.js";
import { DetalheTecnico } from "../../components/DetalheTecnico.js";
import { Area, Botao, Campo, Falha, Girando, Selo, Vazio, cx } from "../../components/ui.js";
import { useAuth } from "../auth/useAuth.js";
import { useLote } from "./useLote.js";
import { AnaliseDaFoto } from "./AnaliseDaFoto.js";
import { RegerarComPrompt } from "./RegerarComPrompt.js";
import { ESTADOS, ordemDe } from "./estados.js";
import type { EstadoDoItem } from "@foto/shared";

type Lote = SaidaRota["lotes"]["getById"];
type Item = Lote["itens"][number];

/**
 * Um lote, foto a foto.
 *
 * A tela existe para a decisão que a máquina não toma: aprovar o que ficou bom.
 * Por isso os itens que pedem atenção vêm primeiro e os aprovados por último —
 * a ordem é a da pendência, não a do id.
 */
export function LotePage() {
  const { loteId } = useParams<{ loteId: string }>();
  const { lote, carregando, erro, recarregar } = useLote(loteId);

  const utils = trpc.useUtils();
  const [avisoErro, setErro] = useState<string | null>(null);
  const [aberto, setAberto] = useState<number | null>(null);
  /** O item cujo prompt o admin está revendo antes de gerar outra. */
  const [revendo, setRevendo] = useState<number | null>(null);
  const { ehAdmin } = useAuth();

  /**
   * O estado que a grade mostra, no endereço.
   *
   * Num lote de cinquenta, achar as vinte e cinco que falharam era rolar a
   * grade inteira. Na URL, o voltar do navegador desfaz o filtro e recarregar
   * mantém a vista. Valor que não é estado — um link antigo, digitado à mão —
   * vale como sem filtro.
   */
  const [parametrosDaUrl, setParametrosDaUrl] = useSearchParams();
  const pedido = parametrosDaUrl.get("estado");
  const filtro: EstadoDoItem | null =
    pedido !== null && pedido in ESTADOS ? (pedido as EstadoDoItem) : null;
  const filtrar = (e: EstadoDoItem | null) =>
    setParametrosDaUrl(
      (atuais) => {
        const novos = new URLSearchParams(atuais);
        if (e === null) novos.delete("estado");
        else novos.set("estado", e);
        return novos;
      },
      { replace: false },
    );
  // Uma confirmação aberta por vez: duas caixas vermelhas empilhadas não
  // deixariam claro qual botão responde a qual pergunta.
  const [confirmando, setConfirmando] = useState<"cancelar" | "abortar" | "reprocessar" | null>(
    null,
  );
  const alternar = (qual: "cancelar" | "abortar" | "reprocessar") =>
    setConfirmando((atual) => (atual === qual ? null : qual));

  const invalidar = async () => {
    if (loteId !== undefined) await utils.lotes.getById.invalidate({ loteId });
  };
  const aoFalhar = (e: { message: string }) => setErro(e.message);

  const aprovarTodos = trpc.lotes.aprovarTodosOk.useMutation({
    onSuccess: invalidar,
    onError: aoFalhar,
  });
  const publicar = trpc.lotes.publicarAprovadas.useMutation({
    onSuccess: invalidar,
    onError: aoFalhar,
  });
  const retomar = trpc.lotes.retomar.useMutation({ onSuccess: invalidar, onError: aoFalhar });
  const cancelar = trpc.lotes.cancelarPendentes.useMutation({
    onSuccess: async () => {
      setConfirmando(null);
      await invalidar();
    },
    onError: aoFalhar,
  });
  const abortar = trpc.lotes.abortar.useMutation({
    onSuccess: async () => {
      setConfirmando(null);
      await invalidar();
    },
    onError: aoFalhar,
  });
  const reprocessar = trpc.lotes.reprocessar.useMutation({
    onSuccess: async () => {
      setConfirmando(null);
      await invalidar();
    },
    onError: aoFalhar,
  });
  const aprovar = trpc.generations.approveImage.useMutation({
    onSuccess: invalidar,
    onError: aoFalhar,
  });
  const rejeitar = trpc.generations.rejectImage.useMutation({
    onSuccess: invalidar,
    onError: aoFalhar,
  });
  const refazer = trpc.lotes.refazerDoInicio.useMutation({
    onSuccess: async () => {
      setRevendo(null);
      await invalidar();
    },
    onError: aoFalhar,
  });
  const regerar = trpc.generations.regenerate.useMutation({
    onSuccess: async () => {
      setRevendo(null);
      await invalidar();
    },
    onError: aoFalhar,
  });

  if (carregando) {
    return (
      <Pagina titulo="Lote">
        <div className="flex justify-center py-12">
          <Girando className="size-6 text-texto-fraco" />
        </div>
      </Pagina>
    );
  }

  if (erro !== null || lote === undefined) {
    return (
      <Pagina titulo="Lote">
        <Falha mensagem={erro ?? "Lote não encontrado"} tentarNovamente={recarregar} />
        <div className="mt-4">
          <Link to="/lotes">
            <Botao tom="fantasma" formato="nu">
              ‹ Todos os lotes
            </Botao>
          </Link>
        </div>
      </Pagina>
    );
  }

  const { resumo, parametros } = lote;
  const emCurso =
    resumo.porEstado.analisando + resumo.porEstado.gerando + resumo.porEstado.conferindo;
  /** O que ainda não tem imagem: é o que o abortar alcança. */
  const semImagem =
    resumo.porEstado.sem_saldo + resumo.porEstado.analisando + resumo.porEstado.gerando;
  const decididas = resumo.porEstado.aprovado;
  const itens = [...lote.itens].sort(
    (a, b) => ordemDe(a.estado) - ordemDe(b.estado) || a.id - b.id,
  );
  const visiveis = filtro === null ? itens : itens.filter((i) => i.estado === filtro);

  const custo = lote.itens.reduce(
    (soma, i) => soma + (i.analise?.custoUsd ?? 0) + (i.conferencia?.custoUsd ?? 0),
    0,
  );

  /**
   * A identidade do lote numa linha só.
   *
   * O número vem primeiro porque é por ele que se fala do lote — na Biblioteca,
   * no WhatsApp da casa, na conversa. Depois o onde, que é o que a pessoa lembra
   * quando não lembra o número.
   */
  const identidade = [
    parametros?.numero ? `Lote #${parametros.numero}` : null,
    lote.instituicao,
    lote.vivencia,
    `${resumo.total} ${resumo.total === 1 ? "foto" : "fotos"}`,
    `criado em ${dataHora(lote.criadoEm)}`,
    parametros?.personagem ? parametros.personagem.nome : null,
  ]
    .filter((p): p is string => p !== null)
    .join(" · ");

  return (
    <Pagina
      titulo={parametros?.nome ?? "Lote"}
      descricao={identidade}
      acao={
        <div className="flex gap-2">
          <Link
            to={`/biblioteca?lote=${encodeURIComponent(lote.loteId)}${
              parametros?.numero ? `&n=${parametros.numero}` : ""
            }`}
          >
            <Botao tom="fantasma">Ver na Biblioteca</Botao>
          </Link>
          <Link to="/lotes">
            <Botao tom="fantasma">Todos os lotes</Botao>
          </Link>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        {avisoErro && <Falha mensagem={avisoErro} tentarNovamente={() => setErro(null)} />}

        <section className="rounded-peca border border-borda bg-superficie px-4 py-3">
          <div className="mb-2 flex items-baseline justify-between gap-3">
            <span className="text-[0.9375rem] text-texto">
              {emCurso > 0 ? (
                <>
                  <span className="num font-medium">{emCurso}</span> em andamento
                </>
              ) : (
                "Nada em andamento"
              )}
            </span>
            <span className="text-[0.8125rem] text-texto-suave">
              <span className="num font-mono">{decididas}</span> de{" "}
              <span className="num font-mono">{resumo.total}</span> aprovadas
            </span>
          </div>

          <div className="h-1 overflow-hidden rounded-full bg-borda">
            <div
              className={cx(
                "h-full rounded-full transition-[width] duration-500",
                emCurso > 0 ? "bg-projecao" : "bg-fixado",
              )}
              style={{ width: `${resumo.total === 0 ? 0 : (decididas / resumo.total) * 100}%` }}
            />
          </div>

          {/* Os selos são o filtro: quem lê "25 falharam" quer ver as vinte e
              cinco. O ativo ganha um aro na própria cor; os outros recuam, mas
              continuam tocáveis para trocar de filtro sem voltar ao todo. */}
          <div
            role="group"
            aria-label="Mostrar só as fotos de um estado"
            className="mt-3 flex flex-wrap items-center gap-2"
          >
            {(Object.keys(resumo.porEstado) as EstadoDoItem[])
              .filter((e) => resumo.porEstado[e] > 0)
              .map((e) => {
                const ativo = filtro === e;
                return (
                  <button
                    key={e}
                    type="button"
                    aria-pressed={ativo}
                    onClick={() => filtrar(ativo ? null : e)}
                    // A área de toque passa da altura do selo sem mudar o
                    // desenho da linha: o polegar erra um alvo de 24 px.
                    className={cx(
                      "-my-1.5 rounded-full py-1.5 transition-opacity focus-visible:rounded-full",
                      filtro !== null && !ativo && "opacity-45 hover:opacity-100",
                    )}
                  >
                    <Selo tom={ESTADOS[e].tom} className={cx(ativo && "ring-1 ring-current")}>
                      {resumo.porEstado[e]} {ESTADOS[e].contagem(resumo.porEstado[e])}
                    </Selo>
                  </button>
                );
              })}
            {filtro !== null && (
              <Botao
                tom="fantasma"
                formato="nu"
                className="text-[0.8125rem]!"
                onClick={() => filtrar(null)}
              >
                Mostrar as {resumo.total}
              </Botao>
            )}
            {custo > 0 && (
              <span className="text-[0.75rem] text-texto-fraco">
                análise e conferência: {dolar(custo)}
              </span>
            )}
          </div>
        </section>

        <div className="flex flex-wrap gap-2">
          {resumo.aprovaveis > 0 && (
            <Botao
              tom="brasa"
              carregando={aprovarTodos.isPending}
              onClick={() => loteId && aprovarTodos.mutate({ loteId })}
            >
              Aprovar {resumo.aprovaveis} sem ressalva
            </Botao>
          )}

          {/* Só um preenchimento sólido por tela: enquanto há o que aprovar, é o
              aprovar que leva o âmbar, e publicar espera a sua vez. */}
          {lote.publicaveis > 0 && (
            <Botao
              tom={resumo.aprovaveis > 0 ? "quieto" : "brasa"}
              carregando={publicar.isPending}
              onClick={() => loteId && publicar.mutate({ loteId })}
            >
              Publicar {lote.publicaveis} {lote.publicaveis === 1 ? "aprovada" : "aprovadas"}
            </Botao>
          )}

          {resumo.porEstado.sem_saldo > 0 && (
            <Botao
              tom="quieto"
              carregando={retomar.isPending}
              onClick={() => loteId && retomar.mutate({ loteId })}
            >
              Retomar {resumo.porEstado.sem_saldo} parada
              {resumo.porEstado.sem_saldo > 1 ? "s" : ""}
            </Botao>
          )}

          {(resumo.porEstado.sem_saldo > 0 || resumo.porEstado.analisando > 0) && (
            <Botao tom="fantasma" onClick={() => alternar("cancelar")}>
              {confirmando === "cancelar" ? "Não cancelar" : "Cancelar as que não começaram"}
            </Botao>
          )}

          {semImagem > 0 && (
            <Botao tom="fantasma" onClick={() => alternar("abortar")}>
              {confirmando === "abortar" ? "Não abortar" : "Abortar o lote"}
            </Botao>
          )}

          {lote.reprocessaveis > 0 && (
            <Botao tom="quieto" onClick={() => alternar("reprocessar")}>
              {confirmando === "reprocessar"
                ? "Não reprocessar"
                : `Reprocessar ${lote.reprocessaveis === 1 ? "a" : "as"} ${lote.reprocessaveis} com problema`}
            </Botao>
          )}
        </div>

        {confirmando === "cancelar" && (
          <div className="rounded-peca border border-descartar/40 bg-descartar/10 px-4 py-3">
            <p className="text-[0.9375rem]">
              Remover do lote as fotos que ainda não foram geradas? As já geradas ficam — elas
              custaram e continuam disponíveis.
            </p>
            <div className="mt-3">
              <Botao
                tom="descartar"
                carregando={cancelar.isPending}
                onClick={() => loteId && cancelar.mutate({ loteId })}
              >
                Remover mesmo assim
              </Botao>
            </div>
          </div>
        )}

        {confirmando === "abortar" && (
          <div className="rounded-peca border border-descartar/40 bg-descartar/10 px-4 py-3">
            <p className="text-[0.9375rem]">
              Abortar o lote? As fotos que ainda não foram geradas ficam como abortadas e não gastam
              mais nada. As que o modelo já está desenhando terminam e entram no lote; as prontas
              continuam aqui para aprovar e publicar.
            </p>
            <div className="mt-3">
              <Botao
                tom="descartar"
                carregando={abortar.isPending}
                onClick={() => loteId && abortar.mutate({ loteId })}
              >
                Abortar mesmo assim
              </Botao>
            </div>
          </div>
        )}

        {confirmando === "reprocessar" && parametros && (
          <PainelReprocessar
            quantas={lote.reprocessaveis}
            parametros={parametros}
            carregando={reprocessar.isPending}
            aoReprocessar={(texto) => loteId && reprocessar.mutate({ loteId, ...texto })}
          />
        )}

        {itens.length === 0 ? (
          <Vazio titulo="Lote vazio" descricao="Todas as fotos deste lote foram removidas." />
        ) : visiveis.length === 0 && filtro !== null ? (
          // O estado esvaziou com o filtro aberto — depois de reprocessar as
          // que falharam, por exemplo. A grade vazia sem explicação pareceria
          // um lote que perdeu as fotos.
          <Vazio
            titulo="Nenhuma foto neste estado agora"
            descricao="As fotos deste lote mudaram de estado."
            acao={
              <Botao tom="quieto" onClick={() => filtrar(null)}>
                Mostrar as {resumo.total}
              </Botao>
            }
          />
        ) : (
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {visiveis.map((item) => (
              <li key={item.id}>
                <Cartao
                  item={item}
                  aoAbrir={() => setAberto(item.id)}
                  aprovando={aprovar.isPending && aprovar.variables?.id === item.id}
                  rejeitando={rejeitar.isPending && rejeitar.variables?.id === item.id}
                  regerando={regerar.isPending && regerar.variables?.id === item.id}
                  aoAprovar={() => aprovar.mutate({ id: item.id })}
                  aoRejeitar={() => rejeitar.mutate({ id: item.id })}
                  // O admin revê o prompt antes; quem opera gera direto.
                  aoRegerar={() => {
                    if (!ehAdmin) return regerar.mutate({ id: item.id });
                    regerar.reset();
                    refazer.reset();
                    setRevendo(item.id);
                  }}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      {revendo !== null &&
        (() => {
          const item = lote.itens.find((i) => i.id === revendo);
          if (!item) return null;

          return (
            <Sobreposicao rotulo={`Gerar outra — ${item.nome}`} aoFechar={() => setRevendo(null)}>
              <RegerarComPrompt
                item={item}
                gerando={regerar.isPending}
                refazendo={refazer.isPending}
                falha={
                  regerar.isError
                    ? regerar.error.message
                    : refazer.isError
                      ? refazer.error.message
                      : null
                }
                aoRefazer={() => refazer.mutate({ id: item.id })}
                aoGerar={(prompt) => regerar.mutate({ id: item.id, prompt })}
                aoCancelar={() => setRevendo(null)}
              />
            </Sobreposicao>
          );
        })()}

      {aberto !== null &&
        (() => {
          const item = lote.itens.find((i) => i.id === aberto);
          if (!item) return null;

          return (
            <Sobreposicao rotulo={item.nome} aoFechar={() => setAberto(null)}>
              <Detalhe item={item} />
            </Sobreposicao>
          );
        })()}
    </Pagina>
  );
}

/**
 * As instruções do lote, abertas para corrigir antes de rodar de novo.
 *
 * Abortou-se porque o resultado não ficou bom, ou falhou no provedor;
 * reprocessar com o mesmo texto daria o mesmo resultado. O texto começa com o
 * que o lote usou, e o que mudar aqui vale para as que voltam — as já geradas
 * continuam como estão.
 */
function PainelReprocessar({
  quantas,
  parametros,
  carregando,
  aoReprocessar,
}: {
  quantas: number;
  parametros: NonNullable<Lote["parametros"]>;
  carregando: boolean;
  aoReprocessar: (texto: { pedidoExtra: string; personagemDescricao: string }) => void;
}) {
  const [pedidoExtra, setPedidoExtra] = useState(parametros.pedidoExtra);
  const [descricao, setDescricao] = useState(parametros.personagem?.descricao ?? "");

  return (
    <div className="flex flex-col gap-4 rounded-peca border border-borda bg-superficie px-4 py-3">
      <p className="text-[0.9375rem] leading-relaxed">
        Só as fotos com problema — que falharam ou foram abortadas — voltam, e desde o início:
        análise nova, prompt novo e imagem nova, com este texto e com os modelos de IA atuais. As
        prontas e as aprovadas não são tocadas.
      </p>

      <Campo rotulo="Pedido extra" dica="Vale para todas as fotos que forem reprocessadas.">
        <Area
          value={pedidoExtra}
          onChange={(e) => setPedidoExtra(e.target.value)}
          maxLength={2000}
        />
      </Campo>

      {parametros.personagem && (
        <Campo
          rotulo={`O que mais se sabe sobre ${parametros.personagem.nome}`}
          dica="A aparência é lida das fotos de referência; isto é o que elas não mostram."
        >
          <Area value={descricao} onChange={(e) => setDescricao(e.target.value)} maxLength={2000} />
        </Campo>
      )}

      <div>
        <Botao
          tom="quieto"
          carregando={carregando}
          onClick={() => aoReprocessar({ pedidoExtra, personagemDescricao: descricao })}
        >
          Reprocessar {quantas}
        </Botao>
      </div>
    </div>
  );
}

/**
 * A moldura das miniaturas: 4:5, a proporção em que o lote gera.
 *
 * A foto vai inteira (`object-contain`), e o que sobra vira margem neutra: a
 * miniatura quadrada em `object-cover` cortava cabeça, pés e o personagem na
 * borda — justamente o que se olha para aprovar. A moldura fixa, e não a
 * proporção de cada foto, é o que mantém os cartões da grade na mesma altura.
 * A margem fica no invólucro, e não na imagem, para a original esmaecida não
 * esmaecer a margem junto.
 */
const MOLDURA = "block aspect-[4/5] w-full overflow-hidden rounded-peca bg-superficie-alta";

function Cartao({
  item,
  aoAbrir,
  aprovando,
  rejeitando,
  regerando,
  aoAprovar,
  aoRejeitar,
  aoRegerar,
}: {
  item: Item;
  aoAbrir: () => void;
  aprovando: boolean;
  rejeitando: boolean;
  regerando: boolean;
  aoAprovar: () => void;
  aoRejeitar: () => void;
  aoRegerar: () => void;
}) {
  const estado = ESTADOS[item.estado];
  // "conferindo" também: a imagem já existe, e a conferência é auxílio, não
  // porteira — se ela não voltar, o item não pode ficar sem saída.
  const podeDecidir =
    item.estado === "ok" || item.estado === "suspeito" || item.estado === "conferindo";
  const podeRegerar =
    podeDecidir ||
    item.estado === "falhou" ||
    item.estado === "aprovado" ||
    item.estado === "recusado";

  return (
    <div className="flex h-full flex-col gap-2 rounded-peca border border-borda bg-superficie p-3">
      <button
        onClick={aoAbrir}
        className="grid grid-cols-2 gap-1.5 overflow-hidden rounded-peca"
        aria-label={`Ver ${item.nome}`}
      >
        <span className={MOLDURA}>
          <img
            src={item.urls.originalMini}
            alt=""
            loading="lazy"
            className="size-full object-contain opacity-70"
          />
        </span>
        {item.urls.geradaMini ? (
          <span className={MOLDURA}>
            <img
              src={item.urls.geradaMini}
              alt=""
              loading="lazy"
              className="size-full object-contain"
            />
          </span>
        ) : (
          <div
            className={cx(
              MOLDURA,
              "flex items-center justify-center border border-borda",
              estado.emCurso && "revelando",
            )}
          >
            {estado.emCurso && <Girando className="size-4 text-projecao" />}
          </div>
        )}
      </button>

      <div className="flex items-baseline justify-between gap-2">
        <span className="truncate text-[0.8125rem] text-texto">{item.nome}</span>
        {/* Aprovada ainda não está na galeria; publicada, sim — e é isso que o
            operador precisa ler no card depois de publicar. */}
        <Selo tom={estado.tom}>
          {item.estado === "aprovado" && item.publicada ? "na galeria" : estado.rotulo}
        </Selo>
      </div>

      {/* As tentativas anteriores saíram da grade — um cartão por foto —, e
          continuam na Biblioteca. Dizer o número evita achar que sumiram. */}
      {item.tentativas > 1 && (
        <p className="-mt-1 text-[0.75rem] text-texto-fraco">{item.tentativas}ª tentativa</p>
      )}

      {item.conferencia?.motivos.map((m) => (
        <p key={m} className="text-[0.75rem] leading-snug text-descartar">
          {m}
        </p>
      ))}
      {item.conferencia?.ressalvas.map((r) => (
        <p key={r} className="text-[0.75rem] leading-snug text-texto-fraco">
          {r}
        </p>
      ))}
      {/* A recusa grava a marca no mesmo campo do erro; o selo já diz "recusada". */}
      {item.erro && item.estado !== "recusado" && item.estado !== "abortado" && (
        <div className="flex min-w-0 flex-col gap-1">
          <p className="text-[0.75rem] leading-snug text-descartar wrap-anywhere">{item.erro}</p>
          <DetalheTecnico detalhe={item.erroTecnico} />
        </div>
      )}

      {(podeDecidir || podeRegerar) && (
        <div className="mt-auto flex flex-wrap gap-2 pt-1">
          {podeDecidir && (
            <>
              <Botao tom="brasa" carregando={aprovando} onClick={aoAprovar}>
                Aprovar
              </Botao>
              <Botao tom="fantasma" carregando={rejeitando} onClick={aoRejeitar}>
                Recusar
              </Botao>
            </>
          )}
          {podeRegerar && (
            <Botao tom="fantasma" formato="nu" carregando={regerando} onClick={aoRegerar}>
              Gerar outra
            </Botao>
          )}
        </div>
      )}
    </div>
  );
}

/** A foto grande, com o que a máquina viu e escreveu. */
function Detalhe({ item }: { item: Item }) {
  const descricao = item.analise?.descricao ?? {};

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="grid gap-2 sm:grid-cols-2">
        <figure className="flex flex-col gap-1.5">
          <img src={item.urls.original} alt="" className="w-full rounded-peca object-contain" />
          <figcaption className="text-[0.75rem] text-texto-fraco">Foto original</figcaption>
        </figure>

        {item.urls.gerada && (
          <figure className="flex flex-col gap-1.5">
            <img src={item.urls.gerada} alt="" className="w-full rounded-peca object-contain" />
            <figcaption className="text-[0.75rem] text-texto-fraco">Gerada</figcaption>
          </figure>
        )}
      </div>

      {item.erro && item.estado !== "recusado" && item.estado !== "abortado" && (
        <section className="flex flex-col gap-2 rounded-peca border border-descartar/40 bg-descartar/10 px-4 py-3">
          <p className="text-[0.8125rem] leading-snug text-texto wrap-anywhere">{item.erro}</p>
          <DetalheTecnico detalhe={item.erroTecnico} />
        </section>
      )}

      {item.conferencia && (
        <section className="rounded-peca border border-borda bg-superficie px-4 py-3">
          <p className="mb-2 text-[0.8125rem] font-medium text-texto">A conferência</p>
          <p className="text-[0.8125rem] text-texto-suave">
            <span className="num">{item.conferencia.pessoasOriginais ?? "?"}</span> pessoas na
            original, <span className="num">{item.conferencia.pessoasNoResultado ?? "?"}</span> no
            resultado · proporção {item.conferencia.proporcaoOk ? "certa" : "errada"}
          </p>
          {item.conferencia.motivos.map((m) => (
            <p key={m} className="mt-1 text-[0.8125rem] text-descartar">
              {m}
            </p>
          ))}
          {item.conferencia.ressalvas.map((r) => (
            <p key={r} className="mt-1 text-[0.8125rem] text-texto-fraco">
              {r}
            </p>
          ))}
        </section>
      )}

      <AnaliseDaFoto descricao={descricao} />

      {item.prompt && (
        <details className="rounded-peca border border-borda bg-superficie px-4 py-3">
          <summary className="cursor-pointer text-[0.8125rem] font-medium text-texto">
            O prompt enviado
            {item.analise?.promptOrigem === "montado" && (
              <span className="ml-2 text-[0.75rem] font-normal text-texto-fraco">
                (montado — o modelo não escreveu um)
              </span>
            )}
          </summary>
          <p className="mt-3 whitespace-pre-wrap text-[0.8125rem] leading-relaxed text-texto-suave">
            {item.prompt}
          </p>
        </details>
      )}
    </div>
  );
}
